import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Vendor,
  VendorViewModel,
  VendorListResponse,
  CountryLookup,
  CityLookup,
  ApiResponse,
} from '@/api/types'

/**
 * Vendor management service
 * Handles all vendor-related API operations
 */
export class VendorsService {
  /**
   * Get paginated list of vendors for a branch
   */
  async getAllVendors(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<VendorListResponse> {
    const url = endpoints.vendors.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<VendorListResponse> | VendorListResponse | any>(url)
    const payload = response.data

    // Helper to map vendors array
    const mapVendors = (vendors: any[]): Vendor[] => {
      return vendors.map(vendor => this.mapVendorData(vendor))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapVendors(data),
          }
        }
        return data as VendorListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapVendors(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapVendors(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapVendors(payload.data) : [],
    }
  }

  /**
   * Get vendor by ID
   */
  async getVendorById(branchId: number, vendorId: number): Promise<Vendor> {
    const url = endpoints.vendors.getById(vendorId, branchId)
    const response = await apiClient.get<ApiResponse<Vendor> | Vendor | any>(url)
    
    let vendorData: any
    
    if ('data' in response.data && 'success' in response.data) {
      vendorData = response.data.data
    } else if ('data' in response.data) {
      vendorData = response.data.data
    } else {
      vendorData = response.data
    }
    
    return this.mapVendorData(vendorData)
  }

  /**
   * Save vendor (insert)
   */
  async saveVendor(vendor: VendorViewModel): Promise<Vendor> {
    const url = endpoints.vendors.save
    const response = await apiClient.post<any>(url, vendor)
    const raw = response.data
    const result = raw?.result ?? raw
    if (result?.isError) {
      throw Object.assign(new Error(result.msg || result.message || 'Save failed'), {
        response: { data: raw },
      })
    }
    const id = Number(result?.id ?? result?.Id ?? raw?.vendorId ?? 0)
    return this.mapVendorData({ vendorId: id, id })
  }

  /**
   * Update vendor
   */
  async updateVendor(vendor: VendorViewModel): Promise<Vendor> {
    const url = endpoints.vendors.update
    const response = await apiClient.put<ApiResponse<Vendor> | Vendor>(url, vendor)
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapVendorData((response.data as ApiResponse<Vendor>).data)
    }
    
    return this.mapVendorData(response.data as Vendor)
  }

  /**
   * Delete vendor
   */
  async deleteVendor(vendorId: number, ipAddress: string): Promise<void> {
    const url = endpoints.vendors.delete(vendorId, ipAddress)
    await apiClient.delete(url)
  }

  /**
   * Get country lookups
   */
  async getCountries(): Promise<CountryLookup[]> {
    const url = endpoints.vendors.getCountries
    const response = await apiClient.get<ApiResponse<CountryLookup[]> | CountryLookup[] | any>(url)
    
    let countriesData: any[] = []
    
    if (Array.isArray(response.data)) {
      countriesData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      countriesData = response.data.data
    }
    
    // Map to CountryLookup format
    return countriesData.map((item: any) => ({
      id: item.id || item.countryId || 0,
      lookupNameEn: item.lookupNameEn || item.countryNameEn || item.nameEn || item.name || '',
      lookupNameAr: item.lookupNameAr || item.countryNameAr || item.nameAr || '',
    }))
  }

  /**
   * Get city lookups
   */
  async getCities(countryId?: number): Promise<CityLookup[]> {
    const url = endpoints.vendors.getCities(countryId)
    const response = await apiClient.get<ApiResponse<CityLookup[]> | CityLookup[] | any>(url)
    
    let citiesData: any[] = []
    
    if (Array.isArray(response.data)) {
      citiesData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      citiesData = response.data.data
    }
    
    // Map to CityLookup format
    return citiesData.map((item: any) => ({
      id: item.id || item.cityId || 0,
      lookupNameEn: item.lookupNameEn || item.cityNameEn || item.nameEn || item.name || '',
      lookupNameAr: item.lookupNameAr || item.cityNameAr || item.nameAr || '',
    }))
  }

  /**
   * Get state lookups
   */
  async getStates(): Promise<CityLookup[]> {
    const url = endpoints.vendors.getStates
    const response = await apiClient.get<ApiResponse<CityLookup[]> | CityLookup[] | any>(url)
    
    let statesData: any[] = []
    
    if (Array.isArray(response.data)) {
      statesData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      statesData = response.data.data
    }
    
    // Map to CityLookup format (states use same structure as cities)
    return statesData.map((item: any) => ({
      id: item.id || item.stateId || 0,
      lookupNameEn: item.lookupNameEn || item.stateNameEn || item.nameEn || item.name || '',
      lookupNameAr: item.lookupNameAr || item.stateNameAr || item.nameAr || '',
    }))
  }

  /**
   * Map API response to Vendor interface
   */
  private mapVendorData(vendorData: any): Vendor {
    return {
      vendorId: vendorData.vendorId || vendorData.id || 0,
      id: vendorData.id || vendorData.vendorId || 0,
      vendorNameEn: vendorData.vendorNameEn || vendorData.nameEn || vendorData.name || '',
      vendorNameAr: vendorData.vendorNameAr || vendorData.nameAr || '',
      primaryPerson: vendorData.primaryPerson || '',
      email: vendorData.email || '',
      phoneNo: vendorData.phoneNo || '',
      mobileNo: vendorData.mobileNo || '',
      website: vendorData.website || '',
      address: vendorData.address || '',
      city: vendorData.city || 0,
      state: vendorData.state || 0,
      zipCode: vendorData.zipCode || '',
      country: vendorData.country || 0,
      companyRegistrationNo: vendorData.companyRegistrationNo || '',
      gstNo: vendorData.gstNo || '',
      activeStatus: vendorData.activeStatus !== undefined ? vendorData.activeStatus : true,
      branchId: vendorData.branchId || 0,
      chartOfAccount: vendorData.chartOfAccount || '',
    } as Vendor
  }
}

export const vendorsService = new VendorsService()

